import {
  getTimeZones as getTzdbTimeZones,
  type TimeZone as TzdbTimeZone,
} from "@vvo/tzdb";

export interface TimeZoneOption {
  value: string;
  label: string;
  countryName: string;
  offset: string;
  offsetMinutes: number;
  searchKeywords: string;
}

/**
 * Formats offset minutes into standard GMT±HH:MM string
 */
export function formatGmtOffset(offsetMinutes = 0): string {
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMinutes);
  const hrs = String(Math.floor(abs / 60)).padStart(2, "0");
  const mins = String(abs % 60).padStart(2, "0");
  return `GMT${sign}${hrs}:${mins}`;
}

/**
 * Transforms a tzdb item into TimeZoneOption
 */
function transformTzdbItem(
  tz: TzdbTimeZone,
  targetName?: string,
): TimeZoneOption {
  const name = targetName || tz.name;
  const offset = formatGmtOffset(tz.currentTimeOffsetInMinutes);
  const label = `(${offset}) ${name}`;
  const cleanOffset = offset.replace("GMT", "").trim();

  const keywords = [
    name,
    name.replace(/[/_]/g, " "),
    tz.countryName || "",
    offset,
    cleanOffset,
    cleanOffset.replace(":", ""),
  ]
    .filter(Boolean)
    .join(" ");

  return {
    value: name,
    label,
    countryName: name,
    offset,
    offsetMinutes: tz.currentTimeOffsetInMinutes,
    searchKeywords: keywords,
  };
}

/**
 * Auto-detect user's current timezone identifier
 */
export const getUserTimeZone = (): string => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    const match = getTzdbTimeZones({ includeUtc: true }).find(
      (item) => item.name === tz || (item.group && item.group.includes(tz)),
    );
    return match ? match.name : tz;
  } catch {
    return "UTC";
  }
};

/**
 * Calculates timezone details for a given IANA timezone
 */
export const getTimeZoneDetails = (timeZone: string): TimeZoneOption => {
  const tzdbList = getTzdbTimeZones({ includeUtc: true });
  const match = tzdbList.find(
    (tz) => tz.name === timeZone || (tz.group && tz.group.includes(timeZone)),
  );

  if (match) {
    return transformTzdbItem(match, match.name);
  }

  return {
    value: timeZone,
    label: `(GMT+00:00) ${timeZone}`,
    countryName: timeZone,
    offset: "GMT+00:00",
    offsetMinutes: 0,
    searchKeywords: timeZone.toLowerCase(),
  };
};

let cachedTimeZones: TimeZoneOption[] | null = null;

/**
 * Returns a sorted list of all IANA time zones formatted with GMT offset
 */
export const getAllTimeZones = (): TimeZoneOption[] => {
  if (cachedTimeZones) {
    return cachedTimeZones;
  }

  const results: TimeZoneOption[] = [];
  const seen = new Set<string>();

  for (const tz of getTzdbTimeZones({ includeUtc: true })) {
    const names = [tz.name, ...(tz.group || [])];
    for (const name of names) {
      if (!seen.has(name)) {
        seen.add(name);
        results.push(transformTzdbItem(tz, name));
      }
    }
  }

  if (!seen.has("UTC")) {
    results.push(getTimeZoneDetails("UTC"));
    seen.add("UTC");
  }

  const userTz = getUserTimeZone();
  if (userTz && !seen.has(userTz)) {
    results.push(getTimeZoneDetails(userTz));
    seen.add(userTz);
  }

  results.sort((a, b) =>
    a.offsetMinutes !== b.offsetMinutes
      ? a.offsetMinutes - b.offsetMinutes
      : a.value.localeCompare(b.value),
  );

  cachedTimeZones = results;
  return results;
};

/**
 * Returns a local Date object representing 00:00:00 of "today" in the specified time zone.
 */
export function getTodayInTimeZone(timeZone?: string): Date {
  if (!timeZone) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  try {
    // Format today's date in target timezone as YYYY-MM-DD
    const [year, month, day] = new Intl.DateTimeFormat("en-CA", { timeZone })
      .format(new Date())
      .split("-")
      .map(Number);

    return new Date(year, month - 1, day, 0, 0, 0, 0);
  } catch {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }
}

